import { supabase } from "@/lib/supabase";
import { getCurrentOrgId } from "@/lib/auth";
import { ApparelProduct, ApparelOrder, ApparelOrderItem } from "@/types/models";

class ApparelService {
  // Get all active products for an organization
  async getProducts(organizationId: string): Promise<ApparelProduct[]> {
    try {
      const { data, error } = await supabase
        .from("apparel_products")
        .select("*")
        .eq("organization_id", organizationId)
        .eq("status", "active")
        .order("name");

      if (error) {
        throw error;
      }

      return data as ApparelProduct[];
    } catch (error) {
      console.error("Error fetching apparel products:", error);
      return [];
    }
  }

  // Get all unique categories from products
  async getProductCategories(): Promise<string[]> {
    try {
      const orgId = await getCurrentOrgId();
      if (!orgId) {
        throw new Error("Organization ID not found");
      }

      const { data, error } = await supabase
        .from("apparel_products")
        .select("category")
        .eq("organization_id", orgId)
        .eq("status", "active");

      if (error) {
        throw error;
      }

      // Extract unique categories
      const categories = [...new Set(data.map((item) => item.category))];
      return categories.sort();
    } catch (error) {
      console.error("Error fetching product categories:", error);
      return [];
    }
  }

  // Get a single product by ID
  async getProductById(id: string): Promise<ApparelProduct | null> {
    try {
      const { data, error } = await supabase
        .from("apparel_products")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        throw error;
      }

      return data as ApparelProduct;
    } catch (error) {
      console.error("Error fetching apparel product:", error);
      return null;
    }
  }

  // Create a new product
  async createProduct(product: Omit<ApparelProduct, "id" | "created_at" | "updated_at">): Promise<ApparelProduct | null> {
    try {
      const { data, error } = await supabase
        .from("apparel_products")
        .insert([product])
        .select("*")
        .single();

      if (error) {
        throw error;
      }

      return data as ApparelProduct;
    } catch (error) {
      console.error("Error creating apparel product:", error);
      return null;
    }
  }

  // Update an existing product
  async updateProduct(id: string, updates: Partial<ApparelProduct>): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from("apparel_products")
        .update(updates)
        .eq("id", id);

      if (error) {
        throw error;
      }

      return true;
    } catch (error) {
      console.error("Error updating apparel product:", error);
      return false;
    }
  }

  // Create a new order
  async createOrder(
    order: Omit<ApparelOrder, "id" | "created_at" | "updated_at" | "items">,
    items: Omit<ApparelOrderItem, "id" | "order_id" | "product">[]
  ): Promise<ApparelOrder | null> {
    try {
      // Insert the order
      const { data: orderData, error: orderError } = await supabase
        .from("apparel_orders")
        .insert([order])
        .select("*")
        .single();

      if (orderError) {
        throw orderError;
      }

      const newOrder = orderData as ApparelOrder;

      // Insert the order items
      const orderItemsToInsert = items.map((item) => ({
        ...item,
        order_id: newOrder.id,
      }));

      const { data: itemsData, error: itemsError } = await supabase
        .from("apparel_order_items")
        .insert(orderItemsToInsert)
        .select("*");

      if (itemsError) {
        throw itemsError;
      }

      return newOrder;
    } catch (error) {
      console.error("Error creating apparel order:", error);
      return null;
    }
  }

  // Get all orders for an organization
  async getOrganizationOrders(organizationId: string): Promise<ApparelOrder[]> {
    try {
      const { data, error } = await supabase
        .from("apparel_orders")
        .select(`
          *,
          family:family_id (
            id,
            name
          ),
          student:student_id (
            id,
            first_name,
            last_name
          )
        `)
        .eq("organization_id", organizationId)
        .order("created_at", { ascending: false });

      if (error) {
        throw error;
      }

      return data as ApparelOrder[];
    } catch (error) {
      console.error("Error fetching apparel orders:", error);
      return [];
    }
  }

  // Get a single order by ID
  async getOrderById(id: string): Promise<ApparelOrder | null> {
    try {
      const { data, error } = await supabase
        .from("apparel_orders")
        .select(`
          *,
          items:apparel_order_items (
            *,
            product:product_id (*)
          ),
          family:family_id (
            id,
            name
          ),
          student:student_id (
            id,
            first_name,
            last_name
          )
        `)
        .eq("id", id)
        .single();

      if (error) {
        throw error;
      }

      return data as ApparelOrder;
    } catch (error) {
      console.error("Error fetching apparel order:", error);
      return null;
    }
  }

  // Update an order's status
  async updateOrderStatus(orderId: string, status: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from("apparel_orders")
        .update({ status })
        .eq("id", orderId);

      if (error) {
        throw error;
      }

      return true;
    } catch (error) {
      console.error("Error updating apparel order status:", error);
      return false;
    }
  }

  // Add an order to an invoice
  async addOrderToInvoice(orderId: string, invoiceId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from("apparel_orders")
        .update({ added_to_invoice: true, invoice_id: invoiceId })
        .eq("id", orderId);

      if (error) {
        throw error;
      }

      return true;
    } catch (error) {
      console.error("Error adding apparel order to invoice:", error);
      return false;
    }
  }
}

export default new ApparelService();
